import { useFeature } from "flagged";
import * as React from "react";

import { AUTH_REPORT_VIEW } from "domain/authority";
import { FLAG_AI_TENANT_STATE } from "services/feature/FeatureFlagService";
import { reportService } from "services/report/erasure/ReportService";
import { userSessionService } from "services/user/UserSessionService";

/**
 * Check whether the tenant has any reports. The check runs when the layout mounts, so that the answer is usually ready
 * before the panel is first opened, and again on every open to keep the answer fresh without hiding the old one.
 *
 * Returns undefined while the answer is pending so that callers can avoid rendering content that would change once the
 * answer arrives. When the check isn't applicable, the answer is known immediately: false.
 */
export function useTenantHasReports(isAiPanelOpen: boolean): boolean | undefined {
    const isTenantStateEnabled = Boolean(useFeature(FLAG_AI_TENANT_STATE));
    const hasReportAuths = userSessionService.userHasAllAuthorities([AUTH_REPORT_VIEW]);
    const isCheckApplicable = isTenantStateEnabled && hasReportAuths;
    const [tenantHasReports, setTenantHasReports] = React.useState<boolean | undefined>(undefined);
    const hasChecked = React.useRef(false);

    React.useEffect(() => {
        const isClosingAfterCheck = !isAiPanelOpen && hasChecked.current;
        if (!isCheckApplicable || isClosingAfterCheck) {
            return;
        }
        hasChecked.current = true;
        const abortController = new AbortController();
        reportService
            .fetchReportsExist(abortController)
            .then(setTenantHasReports)
            .catch(() => {
                if (abortController.signal.aborted) {
                    return;
                }
                setTenantHasReports(false);
            });
        return () => abortController.abort();
    }, [isCheckApplicable, isAiPanelOpen]);

    return isCheckApplicable ? tenantHasReports : false;
}
